import React, { useState, useRef } from 'react';
import TopBar from './components/TopBar';
import WindowFrame from './components/WindowFrame';
import Terminal from './components/Terminal';
import PreviewTerminal from './components/PreviewTerminal';
import StartMenu from './components/StartMenu';
import CyberChef from './components/CyberChef';
import TaskManager from './components/TaskManager';
import FileManager from './components/FileManager';
import Dock from './components/Dock';
import NotificationContainer from './components/Notification';
import BackgroundEffects from './components/BackgroundEffects';
import TimelineProfile from './components/TimelineProfile';
import Notes from './components/Notes';
import RootAccess from './components/RootAccess';
import SecureContact from './components/SecureContact';
import BootScreen from './components/BootScreen';
import ContextMenu from './components/ContextMenu';
import DesktopWidgets from './components/DesktopWidgets';
import ResumeViewer from './components/ResumeViewer';
import Projects from './components/Projects';
import WelcomeOverlay from './components/WelcomeOverlay';
import GuidedTour from './components/GuidedTour';
import SystemGuide from './components/SystemGuide';
import './index.css';

import { initialFS } from './utils/fileSystem';

function App() {
  // Boot screen state
  const [bootComplete, setBootComplete] = useState(false);

  // State to track which windows are open
  const [windows, setWindows] = useState({
    about: true,
    projects: false,
    terminal: false,
    cyberchef: false,
    taskmanager: false,
    filemanager: false,
    notes: false,
    root: false,
    texteditor: false,
    contact: false,
    resume: false,
    previewTerminal: false
  });

  // State to track which windows are minimized
  const [minimized, setMinimized] = useState({
    about: false,
    projects: false,
    terminal: false,
    cyberchef: false,
    taskmanager: false,
    filemanager: false,
    notes: false,
    root: false,
    texteditor: false,
    contact: false,
    resume: false,
    previewTerminal: false
  });

  // State for window positions and z-index
  const topBarHeight = 50;  // 8px padding + 42px pill
  const dockHeight = 92;   // 16px bottom offset + 12px padding + 52px icons + 12px padding
  const usableHeight = window.innerHeight - topBarHeight - dockHeight;
  const [windowPositions, setWindowPositions] = useState({
    about: { x: Math.round((window.innerWidth - 1100) / 2), y: Math.round(topBarHeight + (usableHeight - 700) / 2) },
    projects: { x: 150, y: 100 },
    terminal: { x: Math.max(200, window.innerWidth - 750), y: Math.min(400, window.innerHeight - 500) },
    cyberchef: { x: 250, y: 200 },
    taskmanager: { x: 300, y: 100 },
    filemanager: { x: 450, y: 100 },
    notes: { x: window.innerWidth - 350, y: 80 },
    root: { x: 450, y: 250 },
    texteditor: { x: 200, y: 150 },
    contact: { x: 350, y: 100 },
    resume: { x: 300, y: 120 },
    previewTerminal: { x: Math.max(50, window.innerWidth - 800), y: 120 }
  });

  const [windowZIndex, setWindowZIndex] = useState({
    about: 15,
    projects: 10,
    terminal: 12,
    cyberchef: 10,
    taskmanager: 10,
    filemanager: 10,
    notes: 10,
    root: 10,
    texteditor: 10,
    contact: 10,
    resume: 10,
    previewTerminal: 20
  });



  // State for Start Menu
  const [menuOpen, setMenuOpen] = useState(false);
  // Theme state: controls overall color scheme
  const [theme, setTheme] = useState('matrix');

  // Notification system state
  const [notifications, setNotifications] = useState([]);
  let notificationId = 0;

  // Trash Easter egg state
  const [trashClickCount, setTrashClickCount] = useState(0);

  // Root Access State
  const [isRoot, setIsRoot] = useState(false);

  // File System State (Lifted)
  const [fs, setFs] = useState(initialFS);

  // Text Editor State
  const [editorContent, setEditorContent] = useState({ title: 'Untitled', content: '' });

  // Context Menu State
  const [contextMenu, setContextMenu] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Command Preview State (for tool hover in About Me)
  const [commandPreview, setCommandPreview] = useState(null);
  const [currentToolName, setCurrentToolName] = useState(null);
  const [commandDescription, setCommandDescription] = useState(null);

  // Guided Tour State
  const [tourActive, setTourActive] = useState(false);
  const [tourFocusWindow, setTourFocusWindow] = useState(null); // Which window is currently focused in tour
  const [tourSubtitle, setTourSubtitle] = useState(''); // Subtitle for the current tour step
  const [guideMessage, setGuideMessage] = useState(''); // Message for system guide entity
  const [systemState, setSystemState] = useState(''); // System state message
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const [totalTourSteps, setTotalTourSteps] = useState(3);
  const [tourNavFunctions, setTourNavFunctions] = useState(null);
  const tourRef = useRef(null);

  // Ref to track if welcome notification has been shown
  const hasShownWelcome = useRef(false);

  // DISABLED: Notifications now show based on overlay choice
  // Show welcome notification on first load
  // useEffect(() => {
  //   if (!hasShownWelcome.current) {
  //     hasShownWelcome.current = true;
  //     setTimeout(() => {
  //       const welcomeId = Date.now();
  //       const welcomeNotification = { 
  //         id: welcomeId, 
  //         message: '👋 Welcome to Diptanshu Dhawan\'s Portfolio! Explore my projects, skills, and achievements.', 
  //         type: 'info',
  //         duration: 6000
  //       };
  //       setNotifications((prev) => [...prev, welcomeNotification]);
  //     }, 1500);
  //     
  //     // CTF hint notification
  //     setTimeout(() => {
  //       const ctfHintId = Date.now() + 1;
  //       const ctfNotification = { 
  //         id: ctfHintId, 
  //         message: '🎯 Challenge: Can you gain root access to this machine? (HINT: sudo su)', 
  //         type: 'info',
  //         duration: 7000
  //       };
  //       setNotifications((prev) => [...prev, ctfNotification]);
  //     }, 9000);
  //   }
  // }, []);


  // Helper: Open/Close windows
  const toggleWindow = (key, value) => {
    setWindows((prev) => ({ ...prev, [key]: value }));
    if (value) {
      // Restore window if it was minimized
      setMinimized((prev) => ({ ...prev, [key]: false }));
      // Bring window to front when opened
      bringWindowToFront(key);
    }
  };

  // Helper: Minimize window
  const minimizeWindow = (key) => {
    setMinimized((prev) => ({ ...prev, [key]: true }));
  };

  // Bring window to front
  const bringWindowToFront = (key) => {
    setWindowZIndex((prev) => {
      const maxZ = Math.max(...Object.values(prev), 20);
      return {
        ...prev,
        [key]: maxZ + 1
      };
    });
  };

  // Update window position
  const updateWindowPosition = (key, position) => {
    setWindowPositions((prev) => ({ ...prev, [key]: position }));
  };

  // Helper: Handle Menu Clicks
  const handleMenuClick = (key) => {
    toggleWindow(key, true); 
    setMenuOpen(false);      
  };

  // Cycle through theme options
  const cycleTheme = () => {
    const themes = ['cyber', 'matrix', 'diptanshu'];
    const idx = themes.indexOf(theme);
    const next = themes[(idx + 1) % themes.length];
    setTheme(next);
    // Show notification for theme change
    const themeNames = { cyber: 'Phantom', matrix: 'Matrix', diptanshu: 'Nightshade' };
    showNotification(`Theme changed to ${themeNames[next]}`, 'info');
  };

  // Helper: Show notification
  const showNotification = (message, type = 'info', duration = 3000) => {
    const id = notificationId++;
    const newNotification = { id, message, type, duration };
    setNotifications((prev) => [...prev, newNotification]);
  };

  // Helper: Close notification
  const closeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  // Helper: Restore minimized window from dock
  const restoreWindow = (key) => {
    setMinimized((prev) => ({ ...prev, [key]: false }));
    bringWindowToFront(key);
  };

  // Helper: Handle trash clicks with progressive messages
  const handleTrashClick = () => {
    const newCount = trashClickCount + 1;
    setTrashClickCount(newCount);

    if (newCount === 1) {
      showNotification("Trash is empty.", "info", 1500);
    } else if (newCount === 2) {
      showNotification("Still empty.", "info", 1500);
    } else if (newCount === 3) {
      showNotification("Look, I don't have your files.", "warning", 1500);
    } else if (newCount >= 5) {
      showNotification("Stop touching me!", "error", 1500);
      // Reset counter after 5th click
      setTrashClickCount(0);
    }
  };

  // Handle Root Access Success
  const handleRootAccess = () => {
    setIsRoot(true);
    showNotification("🎉 ACCESS GRANTED! Nice work! You clearly know your way around. Let's connect!", "success", 10000);
    
    // Automatically open contact window after a brief delay
    setTimeout(() => {
      toggleWindow('contact', true);
      bringWindowToFront('contact');
    }, 2000);
  };

  // Handle Open File
  const handleOpenFile = (title, content) => {
    setEditorContent({ title, content });
    toggleWindow('texteditor', true);
  };

  // Handle Context Menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Function to show welcome and CTF notifications
  const showWelcomeNotifications = () => {
    if (!hasShownWelcome.current) {
      hasShownWelcome.current = true;
      
      // Welcome notification
      setTimeout(() => {
        const welcomeId = Date.now();
        const welcomeNotification = { 
          id: welcomeId, 
          message: '👋 Welcome to Diptanshu Dhawan\'s Portfolio! Explore my projects, skills, and achievements.', 
          type: 'info',
          duration: 6000
        };
        setNotifications((prev) => [...prev, welcomeNotification]);
      }, 500);
      
      // CTF hint notification
      setTimeout(() => {
        const ctfHintId = Date.now() + 1;
        const ctfNotification = { 
          id: ctfHintId, 
          message: '🎯 Challenge: Can you gain root access to this machine? (HINT: sudo su)', 
          type: 'info',
          duration: 7000
        };
        setNotifications((prev) => [...prev, ctfNotification]);
      }, 7500);
    }
  };

  // Handle Guided Tour Start
  const handleStartTour = () => {
    setTourActive(true);
    // Start the guided tour via the GuidedTour component
    if (window.startGuidedTour) {
      window.startGuidedTour();
    }
    // Notifications will show after tour completes
  };

  // Handle Explore Free - show notifications immediately
  const handleExploreFree = () => {
    // User chose to explore freely - show notifications right away
    showWelcomeNotifications();
  };

  // Handle Tour Completion - show notifications
  const handleTourComplete = () => {
    setTourActive(false);
    setTourFocusWindow(null);
    // Show notifications after tour finishes
    showWelcomeNotifications();
  };

  // Handle Tour Window Opening
  const handleTourOpenWindow = (windowKey) => {
    toggleWindow(windowKey, true);
    bringWindowToFront(windowKey);
    
    // Center the window on screen for tour
    // Get window dimensions from the window configs
    const windowSizes = {
      about: { width: 1100, height: 700 },
      projects: { width: 1100, height: 700 },
      contact: { width: 800, height: 700 }
    };
    
    const size = windowSizes[windowKey];
    if (size) {
      // Use setTimeout to ensure positioning happens after window is rendered
      setTimeout(() => {
        const centerX = (window.innerWidth - size.width) / 2 - 100; // Shift 100px to the left
        const centerY = (window.innerHeight - size.height) / 2 - 30; // -30 to account for top bar
        
        updateWindowPosition(windowKey, { x: centerX, y: centerY });
      }, 100);
    }
  };

  // Handle Tour Window Closing
  const handleTourCloseWindow = (windowKey) => {
    toggleWindow(windowKey, false);
  };

  // Handle Tour Focus Window
  const handleSetTourFocusWindow = (windowKey) => {
    setTourFocusWindow(windowKey);
    setTourActive(windowKey !== null); // Tour is active when there's a focus window
  };

  const handleTourStepChange = (subtitle) => {
    setTourSubtitle(subtitle);
  };

  const handleGuideMessageChange = (message) => {
    setGuideMessage(message);
  };

  const handleCurrentStepChange = (step, total) => {
    setCurrentTourStep(step);
    setTotalTourSteps(total);
  };

  const handleNavigate = (navFunctions) => {
    setTourNavFunctions(() => navFunctions);
  };

  const handleSystemStateChange = (state) => {
    setSystemState(state);
  };

  const handleContextMenuAction = (action) => {
    switch (action) {
      case 'new-folder':
        // Create a new folder in the file system
        setFs(prevFs => {
          const newFs = JSON.parse(JSON.stringify(prevFs));
          const homeNode = newFs.children.home.children.visitor;
          let folderName = 'New Folder';
          let counter = 1;
          while (homeNode.children[folderName]) {
            folderName = `New Folder (${counter})`;
            counter++;
          }
          homeNode.children[folderName] = {
            name: folderName,
            type: 'dir',
            children: {}
          };
          return newFs;
        });
        showNotification(`📂 Created "${fs.children?.home?.children?.visitor?.children?.['New Folder'] ? 'New Folder' : 'New Folder'}"`, 'success');
        break;
      
      case 'open-terminal':
        toggleWindow('terminal', true);
        bringWindowToFront('terminal');
        showNotification('📟 Terminal opened', 'info');
        break;
      
      case 'scan-viruses':
        setIsScanning(true);
        setTimeout(() => {
          setIsScanning(false);
          showNotification('🛡️ Scan complete: No threats found!', 'success', 3000);
        }, 2000);
        break;
      
      case 'view-source':
        const sourceCode = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Diptanshu Dhawan - Portfolio</title>
  </head>
  <body>
    <div id="root">
      <!-- DiptanshuOS Desktop Simulation -->
      <!-- Built with React.js -->
      <!-- Designed by Diptanshu Dhawan -->
    </div>
    <script src="/src/main.jsx"></script>
  </body>
</html>`;
        setEditorContent({ title: 'index.html - Page Source', content: sourceCode });
        toggleWindow('texteditor', true);
        bringWindowToFront('texteditor');
        break;
      
      case 'inspect':
        showNotification('🔍 Use F12 to open DevTools', 'info');
        break;
      
      case 'refresh':
        showNotification('🔄 Desktop refreshed', 'info', 1500);
        break;
      
      default:
        break;
    }
  };

  // --- RENDER LOGIC: BOOT SCREEN OR DESKTOP ---
  // Show boot screen first
  if (!bootComplete) {
    return <BootScreen onBootComplete={() => setBootComplete(true)} />;
  }

  // Then show desktop after boot complete
  return (
    <div className={`desktop-container theme-${theme}`}>
      
      {/* Background Effects */}
      <BackgroundEffects />
      
      {/* Top System Bar */}
      <TopBar onToggleMenu={() => setMenuOpen(!menuOpen)} onCycleTheme={cycleTheme} theme={theme} isTourActive={tourActive} />

      {/* Start Menu Dropdown */}
      <StartMenu 
        isOpen={menuOpen} 
        onOpenWindow={handleMenuClick} 
      />

      {/* Desktop Area */}
      <div 
        className="desktop-area clean-desktop"
        onContextMenu={handleContextMenu}
      >
      </div>

      {/* Desktop Widgets */}
      <DesktopWidgets />

      {/* --- WINDOWS --- */}
      
      {/* 1. About Me Window */}
      <WindowFrame 
        title="whoami.exe" 
        isOpen={windows.about} 
        onClose={() => toggleWindow('about', false)}
        onMinimize={() => minimizeWindow('about')}
        isMinimized={minimized.about}
        position={windowPositions.about}
        onPositionChange={(pos) => updateWindowPosition('about', pos)}
        zIndex={windowZIndex.about}
        onFocus={() => bringWindowToFront('about')}
        width="1100px"
        height="700px"
        isTourActive={tourActive}
        isTourFocusWindow={tourFocusWindow === 'about'}
        tourSubtitle={tourSubtitle}
      >
        <TimelineProfile onToolHover={(cmd, toolName, description) => {
          setCommandPreview(cmd);
          setCurrentToolName(toolName);
          setCommandDescription(description);
          // Open preview terminal when clicking on a tool
          toggleWindow('previewTerminal', true);
          bringWindowToFront('previewTerminal');
        }} />
      </WindowFrame>

      {/* 2. Projects Window */}
      <WindowFrame 
        title="projects" 
        isOpen={windows.projects} 
        onClose={() => toggleWindow('projects', false)}
        onMinimize={() => minimizeWindow('projects')}
        isMinimized={minimized.projects}
        position={windowPositions.projects}
        onPositionChange={(pos) => updateWindowPosition('projects', pos)}
        zIndex={windowZIndex.projects}
        onFocus={() => bringWindowToFront('projects')}
        width="1100px"
        height="700px"
        isTourActive={tourActive}
        isTourFocusWindow={tourFocusWindow === 'projects'}
        tourSubtitle={tourSubtitle}
      >
        <Projects />
      </WindowFrame>

      {/* 3. Terminal Window (Interactive) */}
      <WindowFrame 
        title="Terminal" 
        isOpen={windows.terminal} 
        onClose={() => toggleWindow('terminal', false)}
        onMinimize={() => minimizeWindow('terminal')}
        isMinimized={minimized.terminal}
        position={windowPositions.terminal}
        onPositionChange={(pos) => updateWindowPosition('terminal', pos)}
        zIndex={windowZIndex.terminal}
        onFocus={() => bringWindowToFront('terminal')}
        width="600px"
        height="450px"
      >
        <Terminal isRoot={isRoot} fs={fs} setFs={setFs} toggleWindow={toggleWindow} />
      </WindowFrame>

      {/* 3.5 Preview Terminal Window (Command Demo) */}
      <WindowFrame 
        title="💡 Command Preview - Demo Terminal" 
        isOpen={windows.previewTerminal} 
        onClose={() => toggleWindow('previewTerminal', false)}
        onMinimize={() => minimizeWindow('previewTerminal')}
        isMinimized={minimized.previewTerminal}
        position={windowPositions.previewTerminal}
        onPositionChange={(pos) => updateWindowPosition('previewTerminal', pos)}
        zIndex={windowZIndex.previewTerminal}
        onFocus={() => bringWindowToFront('previewTerminal')}
        width="700px"
        height="350px"
      >
        <PreviewTerminal command={commandPreview} toolName={currentToolName} description={commandDescription} />
      </WindowFrame>

      {/* 4. Diptanshu's SwissArmy Tool Window */}
      <WindowFrame 
        title="Diptanshu's SwissArmy" 
        isOpen={windows.cyberchef} 
        onClose={() => toggleWindow('cyberchef', false)}
        onMinimize={() => minimizeWindow('cyberchef')}
        isMinimized={minimized.cyberchef}
        position={windowPositions.cyberchef}
        onPositionChange={(pos) => updateWindowPosition('cyberchef', pos)}
        zIndex={windowZIndex.cyberchef}
        onFocus={() => bringWindowToFront('cyberchef')}
        width="750px"
        height="550px"
      >
        <CyberChef />
      </WindowFrame>

      {/* 5. Task Manager Window */}
      <WindowFrame 
        title="Task Manager" 
        isOpen={windows.taskmanager} 
        onClose={() => toggleWindow('taskmanager', false)}
        onMinimize={() => minimizeWindow('taskmanager')}
        isMinimized={minimized.taskmanager}
        position={windowPositions.taskmanager}
        onPositionChange={(pos) => updateWindowPosition('taskmanager', pos)}
        zIndex={windowZIndex.taskmanager}
        onFocus={() => bringWindowToFront('taskmanager')}
        width="700px"
        height="500px"
      >
        <TaskManager windows={windows} onCloseWindow={(key) => toggleWindow(key, false)} />
      </WindowFrame>

      <WindowFrame 
        title="Files - File Manager" 
        isOpen={windows.filemanager} 
        onClose={() => toggleWindow('filemanager', false)}
        onMinimize={() => minimizeWindow('filemanager')}
        isMinimized={minimized.filemanager}
        position={windowPositions.filemanager}
        onPositionChange={(pos) => updateWindowPosition('filemanager', pos)}
        zIndex={windowZIndex.filemanager}
        onFocus={() => bringWindowToFront('filemanager')}
        width="900px"
        height="650px"
      >
        <FileManager fs={fs} setFs={setFs} onOpenFile={handleOpenFile} />
      </WindowFrame>

      {/* 7. Notes Window */}
      <WindowFrame 
        title="Notes" 
        isOpen={windows.notes} 
        onClose={() => toggleWindow('notes', false)}
        onMinimize={() => minimizeWindow('notes')}
        isMinimized={minimized.notes}
        position={windowPositions.notes}
        onPositionChange={(pos) => updateWindowPosition('notes', pos)}
        zIndex={windowZIndex.notes}
        onFocus={() => bringWindowToFront('notes')}
        width="300px"
        height="350px"
      >
        <Notes />
      </WindowFrame>

      {/* 8. Root Access Window */}
      <WindowFrame 
        title="Root Access" 
        isOpen={windows.root} 
        onClose={() => toggleWindow('root', false)}
        onMinimize={() => minimizeWindow('root')}
        isMinimized={minimized.root}
        position={windowPositions.root}
        onPositionChange={(pos) => updateWindowPosition('root', pos)}
        zIndex={windowZIndex.root}
        onFocus={() => bringWindowToFront('root')}
        width="480px"
        height="550px"
        isTourActive={tourActive}
        isTourFocusWindow={tourFocusWindow === 'root'}
      >
        <RootAccess onRootAccess={handleRootAccess} />
      </WindowFrame>

      {/* 9. Text Editor Window */}
      <WindowFrame 
        title={editorContent.title} 
        isOpen={windows.texteditor} 
        onClose={() => toggleWindow('texteditor', false)}
        onMinimize={() => minimizeWindow('texteditor')}
        isMinimized={minimized.texteditor}
        position={windowPositions.texteditor}
        onPositionChange={(pos) => updateWindowPosition('texteditor', pos)}
        zIndex={windowZIndex.texteditor}
        onFocus={() => bringWindowToFront('texteditor')}
        width="500px"
        height="400px"
      >
        <div style={{ 
          height: '100%', 
          padding: '10px', 
          background: '#1e1e1e', 
          color: '#e2e8f0', 
          fontFamily: 'monospace', 
          whiteSpace: 'pre-wrap', 
          overflowY: 'auto' 
        }}>
          {editorContent.content}
        </div>
      </WindowFrame>

      {/* 10. Contact Me Window */}
      <WindowFrame 
        title="Contact Me - Secure Channel" 
        isOpen={windows.contact} 
        onClose={() => toggleWindow('contact', false)}
        onMinimize={() => minimizeWindow('contact')}
        isMinimized={minimized.contact}
        position={windowPositions.contact}
        onPositionChange={(pos) => updateWindowPosition('contact', pos)}
        zIndex={windowZIndex.contact}
        onFocus={() => bringWindowToFront('contact')}
        width="800px"
        height="700px"
        isTourActive={tourActive}
        isTourFocusWindow={tourFocusWindow === 'contact'}
        tourSubtitle={tourSubtitle}
      >
        <SecureContact />
      </WindowFrame>

      {/* 11. Resume Viewer Window */}
      <WindowFrame 
        title="Resume - Diptanshu Dhawan" 
        isOpen={windows.resume}
        onClose={() => toggleWindow('resume', false)}
        onMinimize={() => minimizeWindow('resume')}
        isMinimized={minimized.resume}
        position={windowPositions.resume}
        onPositionChange={(pos) => updateWindowPosition('resume', pos)}
        zIndex={windowZIndex.resume}
        onFocus={() => bringWindowToFront('resume')}
        width="900px"
        height="700px"
      >
        <ResumeViewer />
      </WindowFrame>

      {/* Dock at bottom */}
      <Dock 
        windows={windows} 
        onToggleWindow={toggleWindow}
        onRestoreWindow={restoreWindow}
        minimized={minimized}
        onTrashClick={handleTrashClick}
      />

      {/* Notification Container */}
      <NotificationContainer 
        notifications={notifications}
        onClose={closeNotification}
      />

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu 
          position={contextMenu}
          onClose={handleCloseContextMenu}
          onAction={handleContextMenuAction}
        />
      )}

      {/* Virus Scan Overlay */}
      {isScanning && (
        <div className="virus-scan-overlay">
          <div className="virus-scan-content">
            <div className="scan-icon">🛡️</div>
            <div className="scan-text">Scanning for viruses...</div>
            <div className="scanning-bar">
              <div className="scanning-progress"></div>
            </div>
            <div className="scan-status">Checking system files...</div>
          </div>
        </div>
      )}

      {/* Welcome Overlay - First Visit */}
      <WelcomeOverlay 
        onStartTour={handleStartTour}
        onExploreFree={handleExploreFree}
      />

      {/* Guided Tour */}
      <GuidedTour 
        ref={tourRef}
        onTourComplete={handleTourComplete}
        onOpenWindow={handleTourOpenWindow}
        onCloseWindow={handleTourCloseWindow}
        onSetFocusWindow={handleSetTourFocusWindow}
        onStepChange={handleTourStepChange}
        onGuideMessageChange={handleGuideMessageChange}
        onCurrentStepChange={handleCurrentStepChange}
        onNavigate={handleNavigate}
        onSystemStateChange={handleSystemStateChange}
      />

      {/* System Guide Entity */}
      <SystemGuide 
        isActive={tourActive}
        message={guideMessage}
        systemState={systemState}
        currentStep={currentTourStep}
        totalSteps={totalTourSteps}
        onNext={tourNavFunctions?.next}
        onPrevious={tourNavFunctions?.previous}
        onExit={tourNavFunctions?.exit}
      />

    </div>
  );
}

export default App;